<script setup lang="ts">
import { StateOfAutocomplete } from '@/constants/components/autocomplete'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useDialogsStore } from '@/store/dialogs/dialogs'
import { DIALOGS } from '@/store/dialogs/dialogs.constants'

import { SuggestedOfAddress } from '@/api/modules/geo/geocode.types'

import DeliverySearchButtonPosition from '@/components/Delivery/DeliverySearchButtonPosition.vue'

const DIALOG_PROPS = {
  maxWidth: 1175,
  height: '706',
  autofocus: false,
}

const DRAWER_PROPS = {
  top: 0,
  zIndex: 9999,
}

const { isMobile } = useResponsive()

const { closeDialog } = useDialogsStore()
const { dialogs } = storeToRefs(useDialogsStore())
const { showSnackbar } = useAppStore()

const searchQuery = ref('')

const component = computed(() => {
  return isMobile.value
    ? resolveComponent('TheNavigationDrawer')
    : resolveComponent('TheDialog')
})

const componentProps = computed(() => {
  return isMobile.value ? DRAWER_PROPS : DIALOG_PROPS
})

const inputRadius = computed(() => (isMobile.value ? 'sm' : 'lg'))

const inputPlaceholder = computed(() => {
  return isMobile.value ? 'Найти город' : 'Поиск города'
})

const onHandleErrorGeoPosition = (message: string) => {
  showSnackbar({ type: SnackbarType.ERROR, message, hyphens: false })
}

const handleGeoData = (address: SuggestedOfAddress | null) => {
  if (address?.city) {
    searchQuery.value = address.city

    return
  }

  onHandleErrorGeoPosition(StateOfAutocomplete.NOT_DETECTED_CITY)
}
</script>

<template>
  <component
    :is="component"
    v-model="dialogs.cityChange.visible"
    v-bind="componentProps"
  >
    <the-top-bar
      v-if="isMobile"
      class="city-top-bar"
      prepend-text="Выберите город"
      prepend-icon="arrow-left"
      @on-click-prepend="closeDialog(DIALOGS.CITY_CHANGE)"
    />

    <div data-e2e="city-list" class="city-select-wrapper">
      <div class="city-select-wrapper__header">
        <div class="city-select-wrapper__search">
          <the-input
            v-model="searchQuery"
            :radius="inputRadius"
            autofocus
            hide-details
            icon="search"
            :disabled="!dialogs.cityChange.visible"
            :placeholder="inputPlaceholder"
          />

          <div v-if="isMobile" class="city-select-wrapper__search-geo">
            <delivery-search-button-position
              class="search-input-group__button-location"
              @set-delivery-address="handleGeoData"
              @on-handle-error-geo-position="
                onHandleErrorGeoPosition(StateOfAutocomplete.BAN_GEO_CITY)
              "
            />
          </div>
        </div>

        <div class="city-select-wrapper__label">Текущий город:</div>

        <the-tag
          v-if="dialogs.cityChange.props?.cityName"
          size="md"
          variant="secondary"
          type="order"
        >
          {{ dialogs.cityChange.props.cityName }}
        </the-tag>
      </div>

      <header-city-select-list :search-query="searchQuery || ''" />
    </div>
  </component>
</template>

<style scoped lang="scss">
.city-top-bar {
  position: sticky;
  top: 0;
  z-index: 1;
  margin-bottom: 4px;
}

.city-select-wrapper {
  height: auto;
  padding: 0 0 0 20px;

  @include media('>=tablet') {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0;
  }

  &__header {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px 0 0;

    @include media('>=tablet') {
      flex-shrink: 0;
      justify-content: flex-start;
      padding: 0;
      margin: 8px 0 24px;
    }
  }

  &__search {
    display: flex;
    gap: 8px;
    order: 2;
    width: 100%;
    margin: 20px 0 12px;

    @include media('>=tablet') {
      order: 0;
      max-width: 420px;
      margin: 0;
      overflow: hidden;
    }

    &-geo {
      width: 44px;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: var(--ui-text-primary);

    @include media('>=tablet') {
      margin: 0 16px 0 44px;
    }
  }
}
</style>
